import React from 'react'
import type { OfimaDocumento, OfimaDocumentoEncabezado } from '../../../types/ofimaPedidos'
import { formatOfimaDate, formatOfimaMoney, labelFormaPagoOfima, ofimaDisplayValue } from '../../../utils/ofimaPedidos'
import OfimaTipoBadge from './OfimaTipoBadge'

interface OfimaDocumentoCardProps {
  documento: OfimaDocumento
  nested?: boolean
}

const ACCENT_CLASS: Record<string, string> = {
  RE: 'border-l-gray-400',
  FE: 'border-l-indigo-500',
  FS: 'border-l-sky-500',
  FR: 'border-l-violet-500',
  NC: 'border-l-amber-500'
}

function field(label: string, value: string, always = false) {
  const shown = ofimaDisplayValue(value)
  if (!always && shown === '—') return null
  return (
    <div key={label}>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className={`text-sm ${shown === '—' ? 'text-gray-400' : 'text-gray-900'}`}>{shown}</dd>
    </div>
  )
}

function encabezadoFields(encabezado: OfimaDocumentoEncabezado) {
  const fields = [
    field('Número', encabezado.nrodcto || '', true),
    field('NIT', encabezado.nit || '', true),
    field('Bruto', formatOfimaMoney(encabezado.bruto), true),
    field('Creación Ofima', formatOfimaDate(encabezado.fechaCreacion)),
    field('Vendedor', encabezado.codven || ''),
    field('Descuento', formatOfimaMoney(encabezado.descuento)),
    field('IVA', formatOfimaMoney(encabezado.ivabruto)),
    field('Remisión', encabezado.remision || ''),
    field('Forma de pago', labelFormaPagoOfima(encabezado.formapago)),
    field('Tipo de venta', encabezado.tipovta || ''),
    field('Centro de costo', encabezado.codcc || '')
  ].filter(Boolean)

  if (encabezado.contado != null) {
    fields.push(field('Contado', encabezado.contado ? 'Sí' : 'No', true))
  }

  return fields
}

const OfimaDocumentoCard: React.FC<OfimaDocumentoCardProps> = ({ documento, nested = false }) => {
  const encabezado = documento.encabezado ?? {}
  const lineas = Array.isArray(documento.lineas) ? documento.lineas : []
  const tipo = encabezado.tipodcto || 'Documento'
  const accent = ACCENT_CLASS[tipo.trim().toUpperCase()] ?? 'border-l-gray-300'

  return (
    <article className={`overflow-hidden rounded-lg border border-gray-200 border-l-4 bg-white ${accent} ${nested ? 'shadow-sm' : ''}`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-3">
        <OfimaTipoBadge tipo={tipo} />
        <h4 className="text-sm font-semibold text-gray-900">
          {encabezado.nrodcto ? `${tipo} ${encabezado.nrodcto}` : tipo}
        </h4>
        {encabezado.vigente != null && (
          <span
            className={`rounded px-1.5 py-0.5 text-xs font-medium ${
              encabezado.vigente ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {encabezado.vigente ? 'Vigente' : 'No vigente'}
          </span>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-3 px-4 py-3 md:grid-cols-4">
        {encabezadoFields(encabezado)}
      </dl>

      {encabezado.nota ? (
        <p className="px-4 pb-3 text-sm text-gray-700">
          <span className="font-medium">Nota: </span>
          {encabezado.nota}
        </p>
      ) : null}

      <div className="overflow-x-auto border-t border-gray-200">
        {lineas.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500">Sin líneas</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  { label: 'Producto', align: 'text-left' },
                  { label: 'Nombre', align: 'text-left' },
                  { label: 'Cant.', align: 'text-left' },
                  { label: 'Unitario', align: 'text-right' },
                  { label: 'Venta', align: 'text-right' },
                  { label: 'IVA', align: 'text-left' },
                  { label: 'Desc.', align: 'text-right' },
                  { label: 'Bodega', align: 'text-left' }
                ].map((column) => (
                  <th
                    key={column.label}
                    className={`px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 ${column.align}`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {lineas.map((linea, index) => (
                <tr key={`${linea.producto ?? 'linea'}-${index}`} className="odd:bg-white even:bg-gray-50/70">
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-900">
                    {ofimaDisplayValue(linea.producto)}
                  </td>
                  <td className="max-w-xs truncate px-3 py-2 text-sm text-gray-900" title={linea.nombre || undefined}>
                    {ofimaDisplayValue(linea.nombre)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                    {linea.cantidad ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-gray-900">
                    {ofimaDisplayValue(formatOfimaMoney(linea.valorUnitario))}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-gray-900">
                    {ofimaDisplayValue(formatOfimaMoney(linea.valorVenta))}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                    {linea.iva ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-gray-900">
                    {ofimaDisplayValue(formatOfimaMoney(linea.descuento))}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                    {ofimaDisplayValue(linea.bodega)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </article>
  )
}

export default OfimaDocumentoCard
